import React, { useState, useEffect, useCallback } from 'react';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import socketService from '../services/socketService';
import soundService from '../services/soundService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet, faClock, faCheckCircle, faChartBar, faTimesCircle,
  faMoneyBillTransfer, faEye, faQuestionCircle, faArrowRightArrowLeft,
  faDownload, faRotate,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../components/shared/DataTable';
import { swalConfirm } from '../components/shared/swalConfirm';
import SearchInput from '../components/shared/SearchInput';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import bankLogos from 'thai-banks-logo/banks-logo.json';
import {
  UiPageHeader, UiFilterPanel, UiStatsGrid, UiButton, UiSelect, StatusBadge,
} from '../components/ui';
import type { StatCardDef, StatusDef } from '../components/ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const bankNameToLogoKey: Record<string, string> = {
  KBANK: 'KBANK', KTB: 'KTB', SCB: 'SCB', BBL: 'BBL', BAY: 'BAY',
  TTB: 'TTB', TMB: 'TTB', GSB: 'GSB', GHB: 'GHB', BAAC: 'BAAC',
  CIMB: 'CIMB', CIMBT: 'CIMB', UOB: 'UOB', UOBT: 'UOB', TISCO: 'TISCO',
  KKP: 'KKP', KKB: 'KKP', LHB: 'LHB', 'LH BANK': 'LHB', ICBC: 'ICBC',
  TCRB: 'TCRB', HSBC: 'HSBC', CITI: 'CITI', IBANK: 'IBANK', ISBT: 'IBANK',
};

interface Payout {
  id: string;
  payout_number: string;
  reference_id?: string;
  amount: number;
  fee_amount: number;
  net_amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'cancelled';
  bank_account_name: string;
  bank_account_number: string;
  bank_name: string;
  slip_url?: string;
  note?: string;
  rejected_reason?: string;
  approved_at?: string;
  completed_at?: string;
  created_at: string;
  created_via: 'portal' | 'api';
}

interface WalletInfo {
  balance: number;
  payout_balance: number;
  total_payout_credit: number;
  total_payout_debit: number;
}

interface PayoutStats {
  pending: number;
  approved: number;
  completed: number;
  rejected: number;
  cancelled: number;
  total: number;
  total_amount: number;
  completed_amount: number;
  pending_amount: number;
}

const PayoutsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [wallet, setWallet] = useState<WalletInfo | null>(null);
  const [stats, setStats] = useState<PayoutStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const getTodayStr = () => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  };

  // Draft vs applied — date applies only on ค้นหา
  const [startDate, setStartDate] = useState(getTodayStr());
  const [endDate, setEndDate] = useState(getTodayStr());
  const [appliedStartDate, setAppliedStartDate] = useState(getTodayStr());
  const [appliedEndDate, setAppliedEndDate] = useState(getTodayStr());

  const [searchTerm, setSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [exporting, setExporting] = useState(false);
  const { merchant } = useAuth();
  const isEmployee = merchant?.role === 'employee' || (merchant as any)?.type === 'employee';
  const canSendCallback = !isEmployee || (merchant as any)?.permissions?.can_send_callback !== false;

  const STATUS_MAP: Record<string, StatusDef> = {
    pending:   { bg: 'bg-amber-100', text: 'text-amber-700', icon: faClock, label: t('payout.pendingApproval') },
    approved:  { bg: 'bg-blue-100', text: 'text-blue-700', icon: faCheckCircle, label: t('payout.approved') },
    completed: { bg: 'bg-green-100', text: 'text-green-700', icon: faCheckCircle, label: t('payout.completed') },
    rejected:  { bg: 'bg-red-100', text: 'text-red-600', icon: faTimesCircle, label: t('payout.rejected') },
    cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', icon: faTimesCircle, label: t('payout.cancelled') },
  };

  useEffect(() => {
    soundService.loadSettings();
  }, []);

  const exportCSV = async () => {
    try {
      setExporting(true);

      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (appliedStartDate) params.append('start_date', appliedStartDate);
      if (appliedEndDate) params.append('end_date', appliedEndDate);

      const response = await merchantApi.get(`/merchants/payouts/export?${params.toString()}`);
      const rows: Payout[] = response.data.data;

      if (!rows.length) {
        toast.warning('ไม่มีข้อมูลสำหรับออกรายงาน');
        return;
      }

      const headers = [
        'Payout Number', 'Reference ID', 'Status',
        'Amount', 'Fee', 'Net Amount',
        'Bank', 'Account Number', 'Account Name',
        'Channel', 'Note', 'Rejected Reason',
        'Created At', 'Approved At', 'Completed At',
      ];

      const escape = (v: any) => {
        const s = v == null ? '' : String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const fmt = (d?: string) =>
        d ? dayjs(d).tz('Asia/Bangkok').format('DD/MM/YYYY HH:mm:ss') : '';

      const csvLines = [
        headers.join(','),
        ...rows.map(p => [
          escape(p.payout_number),
          escape(p.reference_id || ''),
          escape(p.status),
          escape(p.amount),
          escape(p.fee_amount),
          escape(p.net_amount),
          escape(p.bank_name),
          escape(p.bank_account_number),
          escape(p.bank_account_name),
          escape(p.created_via),
          escape(p.note || ''),
          escape(p.rejected_reason || ''),
          escape(fmt(p.created_at)),
          escape(fmt(p.approved_at)),
          escape(fmt(p.completed_at)),
        ].join(',')),
      ];

      const bom = '﻿';
      const blob = new Blob([bom + csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `payouts_${appliedStartDate}_${appliedEndDate}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);

      toast.success(`ออกรายงาน ${rows.length} รายการสำเร็จ`);
    } catch {
      toast.error(t('common.cannotLoadData'));
    } finally {
      setExporting(false);
    }
  };

  const applyStats = useCallback((summary: any) => {
    setStats({
      pending: summary.pending_count || 0,
      approved: summary.approved_count || 0,
      completed: summary.completed_count || 0,
      rejected: summary.rejected_count || 0,
      cancelled: summary.cancelled_count || 0,
      total: summary.total_count || 0,
      total_amount: summary.total_amount || 0,
      completed_amount: summary.completed_amount || 0,
      pending_amount: (summary.pending_amount || 0) + (summary.approved_amount || 0),
    });
  }, []);

  const loadData = useCallback(async (silent = false) => {
    if (appliedStartDate && appliedEndDate && appliedStartDate > appliedEndDate) return;
    try {
      if (!silent) setLoading(true);

      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (appliedStartDate) params.append('start_date', appliedStartDate);
      if (appliedEndDate) params.append('end_date', appliedEndDate);
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', String(itemsPerPage));
      params.append('offset', String((currentPage - 1) * itemsPerPage));

      const payoutsRes = await merchantApi.get(`/merchants/payouts?${params.toString()}`);

      if (payoutsRes.data.success) {
        const payoutsData = payoutsRes.data.data.payouts || [];
        setPayouts(payoutsData);
        setTotalItems(payoutsRes.data.data.total || 0);
        if (payoutsRes.data.data.summary) {
          applyStats(payoutsRes.data.data.summary);
        }
      }

      const walletRes = await merchantApi.get('/merchants/wallet');
      if (walletRes.data.success) {
        const walletData = walletRes.data.data;
        setWallet({
          balance: walletData.balance || 0,
          payout_balance: walletData.payout_balance || 0,
          total_payout_credit: walletData.total_payout_credit || 0,
          total_payout_debit: walletData.total_payout_debit || 0,
        });
      }

    } catch (error: any) {
      toast.error(error.response?.data?.message || t('payout.loadError'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, appliedStartDate, appliedEndDate, searchTerm, currentPage, itemsPerPage, t, applyStats]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, appliedStartDate, appliedEndDate, searchTerm, itemsPerPage]);

  useEffect(() => {
    const merchantId = merchant?.id;

    if (merchantId) {
      socketService.connect(merchantId);
    }

    loadData();

    socketService.on('payout-updated', (data: any) => {
      // ⭐ P2P liquidity payout ไม่แสดงหน้านี้ — ข้าม toast/refetch (หน้า p2p-payouts ฟัง event นี้แยกอยู่แล้ว)
      if (data?.is_p2p_liquidity) return;
      if (data.status === 'approved') {
        toast.info(`Payout ${data.payout_number} ${t('payout.payoutApproved')}`);
      } else if (data.status === 'rejected') {
        toast.error(`Payout ${data.payout_number} ${t('payout.payoutRejected')}`);
      } else if (data.status === 'completed') {
        toast.success(`Payout ${data.payout_number} ${t('payout.payoutCompleted')}`);
      } else if (data.status === 'cancelled') {
        toast.warning(`Payout ${data.payout_number} ${t('payout.payoutCancelled')}`);
      }
      loadData(true);
    });

    socketService.on('payout-created', (data: any) => {
      // ⭐ P2P liquidity payout ไม่เกี่ยวกับหน้านี้ (list กรองที่ backend อยู่แล้ว) — กัน toast/เสียงเด้งจาก payload ย่อของ P2P
      if (data?.is_p2p_liquidity) return;
      soundService.playWithdrawalSound();
      toast.info(
        <div>
          <strong><FontAwesomeIcon icon={faArrowRightArrowLeft} /> {t('payout.newPayout')}</strong>
          <div>{data.payout_number}</div>
          <small>{t('payout.from')} {data.merchant_name} - ฿{data.amount?.toLocaleString()}</small>
        </div>,
        { autoClose: 4000 }
      );
      loadData(true);
    });

    return () => {
      socketService.off('payout-updated');
      socketService.off('payout-created');
    };
  }, [loadData]);

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error(t('order.dateFromError'));
      return;
    }
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setCurrentPage(1);
  };

  const handleReset = () => {
    const today = getTodayStr();
    setStartDate(today);
    setEndDate(today);
    setAppliedStartDate(today);
    setAppliedEndDate(today);
    setStatusFilter('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleCallback = async (payout: Payout) => {
    const confirmed = await swalConfirm({
      title: 'ส่ง Callback?',
      html: `ส่ง Callback สำหรับ<br><strong>${payout.payout_number}</strong><br>อีกครั้ง`,
      confirmText: 'ยืนยัน',
    });
    if (!confirmed) return;
    try {
      const response = await merchantApi.post(`/merchants/payouts/${payout.id}/callback`);
      if (response.data.success) {
        toast.success('ส่ง Callback สำเร็จ');
      } else {
        toast.error(`ส่ง Callback ล้มเหลว: ${response.data.data?.error || response.data.message}`);
      }
    } catch (error: any) {
      toast.error(`ส่ง Callback ล้มเหลว: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm(t('payout.cancelConfirm'))) return;

    try {
      const res = await merchantApi.delete(`/merchants/payouts/${id}`);
      if (res.data.success) {
        toast.success(t('payout.cancelSuccess'));
        loadData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('payout.cancelError'));
    }
  };

  const columns: Column<Payout>[] = [
    {
      key: 'payout_number',
      header: t('payout.payoutNumber'),
      sortable: true,
      render: (payout) => (
        <div className="flex flex-col gap-1">
          <strong className="text-gray-800 dark:text-slate-100">{payout.payout_number}</strong>
          {payout.reference_id && (
            <small className="text-gray-500 dark:text-slate-400">Ref: {payout.reference_id}</small>
          )}
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('common.amount'),
      sortable: true,
      render: (payout) => (
        <div>
          <strong className="text-lg" style={{ color: 'var(--theme-accent)' }}>฿{payout.amount.toLocaleString()}</strong>
          {payout.fee_amount > 0 && (
            <div className="text-[11px] text-amber-600 dark:text-amber-400">
              Fee: ฿{payout.fee_amount.toLocaleString()}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'bank_name',
      header: t('common.bank'),
      sortable: true,
      render: (payout) => {
        const logoKey = bankNameToLogoKey[(payout.bank_name || '').toUpperCase()] as keyof typeof bankLogos | undefined;
        const icon = logoKey ? bankLogos[logoKey]?.icon : null;
        return (
          <div className="flex items-center gap-1.5">
            {icon && <img src={icon} alt={payout.bank_name} className="w-6 h-6 rounded-full object-contain" />}
            <span className="text-gray-700 dark:text-slate-200">{payout.bank_name}</span>
          </div>
        );
      },
    },
    {
      key: 'bank_account_number',
      header: t('common.bankAccount'),
      render: (payout) => (
        <code className="bg-gray-100 dark:bg-slate-700 px-2 py-1 rounded text-sm text-gray-700 dark:text-slate-200">{payout.bank_account_number}</code>
      ),
    },
    {
      key: 'bank_account_name',
      header: t('common.accountName'),
      render: (payout) => (
        <span className="text-gray-700 dark:text-slate-200">{payout.bank_account_name}</span>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      render: (payout) => <StatusBadge status={payout.status} statusMap={STATUS_MAP} />,
    },
    {
      key: 'created_at',
      header: t('common.date'),
      sortable: true,
      render: (payout) => (
        <span className="text-sm text-gray-600 dark:text-slate-300">
          {new Date(payout.created_at).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' })}
        </span>
      ),
    },
    {
      key: 'created_via',
      header: t('payout.channel'),
      render: (payout) => (
        payout.created_via === 'api' ? (
          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs font-medium">API</span>
        ) : (
          <span className="px-2 py-1 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded text-xs font-medium">Portal</span>
        )
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (payout) => (
        <div className="flex items-center gap-2">
          {canSendCallback && payout.status === 'completed' && (
            <button
              onClick={(e) => { e.stopPropagation(); handleCallback(payout); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50"
            >
              Callback
            </button>
          )}
          {payout.status === 'pending' && (
            <button
              onClick={(e) => { e.stopPropagation(); handleCancel(payout.id); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50"
            >
              {t('payout.cancelPayout')}
            </button>
          )}
          {payout.slip_url && (
            <a
              href={payout.slip_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50"
            >
              <FontAwesomeIcon icon={faEye} /> {t('common.slip')}
            </a>
          )}
          {payout.rejected_reason && (
            <button
              onClick={(e) => { e.stopPropagation(); alert(`${t('payout.reason')}: ${payout.rejected_reason}`); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-600"
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
            </button>
          )}
        </div>
      ),
    },
  ];

  const statCards: StatCardDef[] = [
    {
      icon: faWallet,
      iconClassName: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
      label: 'Payout Wallet',
      value: <span className="text-gray-800 dark:text-slate-100">฿{(wallet?.payout_balance || 0).toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">Main: ฿{(wallet?.balance || 0).toLocaleString()}</span>,
    },
    {
      icon: faClock,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: t('payout.pendingApproval'),
      value: <span className="text-gray-800 dark:text-slate-100">{stats?.pending ?? 0}</span>,
      subValue: (stats?.pending_amount ?? 0) > 0
        ? <span className="text-xs text-amber-600 dark:text-amber-400">฿{(stats?.pending_amount ?? 0).toLocaleString()}</span>
        : undefined,
    },
    {
      icon: faCheckCircle,
      iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: t('payout.completed'),
      value: <span className="text-gray-800 dark:text-slate-100">{stats?.completed ?? 0}</span>,
      subValue: <span className="text-xs text-green-600 dark:text-green-400">฿{(stats?.completed_amount ?? 0).toLocaleString()}</span>,
    },
    {
      icon: faChartBar,
      iconClassName: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      label: t('common.all'),
      value: <span className="text-gray-800 dark:text-slate-100">{stats?.total ?? 0}</span>,
      subValue: <span className="text-xs text-purple-600 dark:text-purple-400">฿{(stats?.total_amount ?? 0).toLocaleString()}</span>,
    },
  ];

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faMoneyBillTransfer}
        title={t('payout.managePayout')}
        subtitle={`${t('payout.managePayoutDesc')} • ${totalItems.toLocaleString()} ${t('common.items')}`}
      />

      <UiStatsGrid cards={statCards} loading={loading} cols={4} />

      {/* Filter */}
      <UiFilterPanel
        dateFrom={startDate}
        dateTo={endDate}
        onDateFromChange={setStartDate}
        onDateToChange={setEndDate}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={
          <UiButton
            variant="secondary"
            icon={exporting ? faRotate : faDownload}
            onClick={exportCSV}
            disabled={exporting || payouts.length === 0}
          >
            {exporting ? 'กำลังออกรายงาน...' : 'Export CSV'}
          </UiButton>
        }
      >
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">{t('common.search')}</label>
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('payout.searchPlaceholder')}
          />
        </div>
        <UiSelect
          wrapperClassName="w-fit min-w-[150px]"
          label={t('common.status')}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">{t('payout.allStatus')}</option>
          <option value="pending">{t('payout.pendingApproval')}</option>
          <option value="approved">{t('payout.approved')}</option>
          <option value="completed">{t('payout.completed')}</option>
          <option value="rejected">{t('payout.rejected')}</option>
          <option value="cancelled">{t('payout.cancelled')}</option>
        </UiSelect>
      </UiFilterPanel>

      {/* Payouts DataTable */}
      <DataTable<Payout>
        data={payouts}
        columns={columns}
        loading={loading && payouts.length === 0}
        emptyMessage={t('payout.noPayouts')}
        emptyIcon={<FontAwesomeIcon icon={faMoneyBillTransfer} className="text-5xl text-gray-300 dark:text-slate-600" />}
        keyExtractor={(payout) => payout.id}
        serverPagination={{
          currentPage,
          totalItems,
          pageSize: itemsPerPage,
          onPageChange: setCurrentPage,
          onPageSizeChange: setItemsPerPage,
        }}
        hoverable
      />
    </div>
  );
};

export default PayoutsPage;
